import axios from 'axios';

/**
 * API Client for WorkStream Pro
 *
 * SECURITY: Authentication uses httpOnly cookies (set by backend on login/signup).
 * The cookie is sent automatically because the axios instance is configured with
 * `withCredentials: true`. We deliberately do NOT read or store the JWT in
 * localStorage, which mitigates XSS token-theft.
 *
 * The `user` profile object is cached in localStorage for UX (avoiding a /auth/me
 * round-trip on every page load) — this is non-sensitive and OK to expose.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send/receive httpOnly auth cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only logout on 401 if it's NOT a login/auth-bootstrap request
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthBootstrap = url.includes('/auth/login') || url.includes('/auth/me');
      if (!isAuthBootstrap) {
        const hadUser = localStorage.getItem('user');
        if (hadUser) {
          localStorage.removeItem('user');
          localStorage.removeItem('company_branding');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.patch('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Users API (Admin)
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  resetPassword: (id, password) => api.post(`/users/${id}/reset-password`, { password }),
  delete: (id) => api.delete(`/users/${id}`),
};

// Buildings API
export const buildingsAPI = {
  getAll: () => api.get('/buildings'),
  create: (data) => api.post('/buildings', data),
  delete: (id) => api.delete(`/buildings/${id}`),
};

// Floors API
export const floorsAPI = {
  getAll: (buildingId) => api.get('/floors', { params: { building_id: buildingId } }),
  create: (data) => api.post('/floors', data),
  delete: (id) => api.delete(`/floors/${id}`),
};

// Places API
export const placesAPI = {
  getAll: (params) => api.get('/places', { params }),
  create: (data) => api.post('/places', data),
  delete: (id) => api.delete(`/places/${id}`),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Inventory API
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.patch(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  recordUsage: (data) => api.post('/inventory/usage', data),
  getUsage: (itemId) => api.get('/inventory/usage', { params: { item_id: itemId } }),
};

// Tasks API
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getOne: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.patch(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  pickup: (id) => api.post(`/tasks/${id}/pickup`),
  
  // Images - now with image_type (before/after)
  addImage: (taskId, data) => api.post(`/tasks/${taskId}/images`, data),
  updateImageCaption: (taskId, imageId, caption, imageType = 'before') => 
    api.patch(`/tasks/${taskId}/images/${imageId}`, { caption, image_type: imageType }),
  deleteImage: (taskId, imageId, imageType = 'before') => 
    api.delete(`/tasks/${taskId}/images/${imageId}`, { params: { image_type: imageType } }),
  
  // Milestones
  addMilestone: (taskId, data) => api.post(`/tasks/${taskId}/milestones`, data),
  updateMilestone: (taskId, milestoneId, data) => api.patch(`/tasks/${taskId}/milestones/${milestoneId}`, data),
  deleteMilestone: (taskId, milestoneId) => api.delete(`/tasks/${taskId}/milestones/${milestoneId}`),
  
  // Parts
  addPart: (taskId, data) => api.post(`/tasks/${taskId}/parts`, data),
  removePart: (taskId, partId) => api.delete(`/tasks/${taskId}/parts/${partId}`),
  getParts: (taskId) => api.get(`/tasks/${taskId}/parts`),
  
  // Notes
  addNote: (taskId, data) => api.post(`/tasks/${taskId}/notes`, data),
  deleteNote: (taskId, noteId) => api.delete(`/tasks/${taskId}/notes/${noteId}`),
  getNotes: (taskId) => api.get(`/tasks/${taskId}/notes`),
  
  // Documents
  addDocument: (taskId, data) => api.post(`/tasks/${taskId}/documents`, data),
  deleteDocument: (taskId, docId) => api.delete(`/tasks/${taskId}/documents/${docId}`),
  
  // Activity Log
  getActivities: (taskId) => api.get(`/tasks/${taskId}/activities`),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications/clear-all'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Settings/Branding API
export const settingsAPI = {
  getBranding: () => api.get('/settings/branding'),
  updateBranding: (data) => api.put('/settings/branding', data),
};

// Custom Fields API
export const customFieldsAPI = {
  getAll: () => api.get('/custom-fields'),
  create: (data) => api.post('/custom-fields', data),
  update: (id, data) => api.patch(`/custom-fields/${id}`, data),
  delete: (id) => api.delete(`/custom-fields/${id}`),
};

// Backup API
export const backupAPI = {
  export: () => api.get('/backup/export'),
  import: (data) => api.post('/backup/import', data),
};

// Migration API
export const migrationAPI = {
  migrateWorkOrderNumbers: () => api.post('/migrate/work-order-numbers'),
};

// Time Logs API
export const timeLogsAPI = {
  create: (data) => api.post('/time-logs', data),
  getAll: (params) => api.get('/time-logs', { params }),
  getByDate: (date) => api.get('/time-logs', { params: { date } }),
  getWeeklyReport: (startDate, endDate) => api.get('/time-logs/weekly-report', { params: { start_date: startDate, end_date: endDate } }),
  update: (id, data) => api.patch(`/time-logs/${id}`, data),
  delete: (id) => api.delete(`/time-logs/${id}`),
};

export const translationAPI = {
  translate: (texts, targetLanguage) => api.post('/translate', { texts, target_language: targetLanguage }),
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  getAll: () => api.get('/purchase-orders'),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
};

// PM Schedules API
export const pmSchedulesAPI = {
  getAll: () => api.get('/pm-schedules'),
  create: (data) => api.post('/pm-schedules', data),
  update: (id, data) => api.put(`/pm-schedules/${id}`, data),
  delete: (id) => api.delete(`/pm-schedules/${id}`),
  generateWorkOrders: () => api.post('/pm-schedules/generate-work-orders'),
};

// Webhooks API
export const webhooksAPI = {
  getConfig: () => api.get('/webhooks/config'),
  updateConfig: (data) => api.put('/webhooks/config', data),
  test: (data) => api.post('/webhooks/test', data),
};

// Suppliers API
export const suppliersAPI = {
  getAll: () => api.get('/suppliers'),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.patch(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Landing Page API
export const landingPageAPI = {
  get: () => api.get('/landing-page/content/raw'),
  update: (data) => api.put('/landing-page/content', data),
};

export default api;
